import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService, paymentService, cryptoPaymentService } from '../services/api';
import type { CashPackage } from '../types';
import '../styles/Donate.css';

const Donate: React.FC = () => {
    const navigate = useNavigate();
    const [packages, setPackages] = useState<CashPackage[]>([]);
    const [loading, setLoading] = useState(true);
    const [buyingId, setBuyingId] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [selectedId, setSelectedId] = useState<number | null>(null);

    useEffect(() => {
        if (!authService.isAuthenticated()) {
            navigate('/login');
            return;
        }

        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const list = await paymentService.getPackages();
                setPackages(Array.isArray(list) ? list : []);
            } catch {
                setPackages([]);
                setError('Falha ao carregar os pacotes.');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [navigate]);

    const handlePay = async () => {
        const pkg = packages.find(p => p.id === selectedId);
        if (!pkg) {
            setError('Selecione um pacote para continuar.');
            return;
        }

        if (!authService.isAuthenticated()) {
            navigate('/login');
            return;
        }

        setBuyingId(pkg.id);
        setError(null);
        try {
            const pref = await paymentService.createPreference(pkg.id);
            const url =
                pref?.paymentUrl ||
                (pref as any)?.PaymentUrl ||
                pref?.sandboxUrl ||
                (pref as any)?.SandboxUrl;

            if (url) {
                window.location.href = url;
                return;
            }
            setError('Não foi possível iniciar o pagamento.');
        } catch (e: any) {
            setError(e?.response?.data?.message || 'Erro ao iniciar o pagamento.');
        } finally {
            setBuyingId(null);
        }
    };

    const handlePayCrypto = async () => {
        const pkg = packages.find(p => p.id === selectedId);
        if (!pkg) {
            setError('Selecione um pacote para continuar.');
            return;
        }

        if (!authService.isAuthenticated()) {
            navigate('/login');
            return;
        }

        setBuyingId(pkg.id);
        setError(null);
        try {
            const payment = await cryptoPaymentService.createPayment(pkg.id);

            if (payment?.payUrl) {
                window.location.href = payment.payUrl;
                return;
            }

            if (payment?.payAddress) {
                setError(`Envie exatamente ${payment.payCurrency ? payment.payCurrency.toUpperCase() : ''} para o endereço: ${payment.payAddress}`);
                return;
            }

            setError('Não foi possível iniciar o pagamento em criptomoeda.');
        } catch (e: any) {
            setError(e?.response?.data?.message || 'Erro ao iniciar o pagamento em criptomoeda.');
        } finally {
            setBuyingId(null);
        }
    };

    const getTotalCash = (p: CashPackage) =>
        p.totalCashAmount ?? (p.cashAmount + (p.bonusCashAmount ?? 0));

    const getBonusPercent = (p: CashPackage) => {
        const bonus = p.bonusCashAmount ?? 0;
        if (!bonus || !p.cashAmount) return 0;
        return Math.round((bonus / p.cashAmount) * 100);
    };

    return (
        <div className="donate-page">
            <div className="container">
                <header className="donate-header">
                    <div>
                        <h1 className="donate-title">Doação / Cash</h1>
                        <p className="donate-subtitle">
                            Escolha um pacote de Cash e finalize o pagamento com segurança pelo Mercado Pago ou em criptomoeda.
                        </p>
                    </div>
                    <div className="donate-provider">
                        <span className="donate-provider-label">Processado por</span>
                        <span className="donate-provider-name">Mercado Pago</span>
                    </div>
                </header>

                {error && <div className="donate-error">{error}</div>}

                {loading ? (
                    <p className="donate-loading">Carregando pacotes de doação...</p>
                ) : (
                    <>
                        <div className="donate-table">
                            <div className="donate-row donate-row-head">
                                <div>Pacote</div>
                                <div>Bônus</div>
                                <div>Total</div>
                                <div>Valor (R$)</div>
                                <div></div>
                            </div>
                            {packages.map((p) => {
                                const isSelected = selectedId === p.id;
                                const bonus = p.bonusCashAmount ?? 0;
                                const total = getTotalCash(p);
                                const bonusPct = getBonusPercent(p);
                                const pricePerCash = total > 0 ? p.price / total : 0;

                                return (
                                    <button
                                        key={p.id}
                                        type="button"
                                        className={`donate-row donate-row-body ${isSelected ? 'selected' : ''}`}
                                        onClick={() => setSelectedId(p.id)}
                                    >
                                        <div className="donate-cash">
                                            <span className="donate-cash-amount">
                                                {p.cashAmount.toLocaleString('pt-BR')} Cash
                                            </span>
                                            {bonusPct >= 100 && (
                                                <span className="donate-tag recommend">100% bônus</span>
                                            )}
                                        </div>
                                        <div className="donate-bonus">
                                            {bonus > 0 ? (
                                                <>
                                                    <span className="donate-bonus-amount">
                                                        +{bonus.toLocaleString('pt-BR')} Cash
                                                    </span>
                                                    {bonusPct > 0 && (
                                                        <span className="donate-bonus-pct">({bonusPct}%)</span>
                                                    )}
                                                </>
                                            ) : (
                                                <span className="donate-bonus-none">—</span>
                                            )}
                                        </div>
                                        <div className="donate-total">
                                            <span className="donate-total-amount">
                                                {total.toLocaleString('pt-BR')} Cash
                                            </span>
                                        </div>
                                        <div className="donate-price">
                                            <span className="donate-price-main">
                                                R$ {p.price.toFixed(2).replace('.', ',')}
                                            </span>
                                            <span className="donate-price-per">
                                                R$ {pricePerCash.toFixed(2).replace('.', ',')} por Cash
                                            </span>
                                        </div>
                                        <div className="donate-action">
                                            <span className="donate-select-hint">
                                                {isSelected ? 'Selecionado' : 'Selecionar'}
                                            </span>
                                        </div>
                                    </button>
                                );
                            })}
                            {packages.length === 0 && (
                                <p className="donate-empty">Nenhum pacote disponível no momento.</p>
                            )}
                        </div>

                        <div className="donate-footer">
                            <button
                                type="button"
                                className="donate-btn-primary"
                                disabled={buyingId !== null || !selectedId}
                                onClick={handlePay}
                            >
                                {buyingId !== null ? 'Redirecionando...' : 'Continuar para pagamento'}
                            </button>
                            <button
                                type="button"
                                className="donate-btn-secondary"
                                disabled
                                title="Em construção — em breve"
                            >
                                Pagamento em Cripto (em breve)
                            </button>
                            <p className="donate-safe-text">
                                Mercado Pago: você será redirecionado para o ambiente seguro do Mercado Pago.
                                O cash base e o bônus são creditados automaticamente após aprovação.
                                Pagamento em Cripto (BTCPay Server): em construção, disponível em breve.
                            </p>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default Donate;
